import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ScrollText, TrendingUp, CalendarCheck, Clock, ArrowRight } from "lucide-react";
import { PRICING_PAGE, DEMO_PAGE } from "@/constants/testIds";
import { Footer } from "@/components/Footer";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal tracking-tight">CERH</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link data-testid={DEMO_PAGE.navLink} to="/demo" className="text-sm font-medium text-charcoal/70 hover:text-charcoal transition-colors px-3 sm:px-4 py-2">
            Ver demo
          </Link>
          <Link data-testid={PRICING_PAGE.navLink} to="/precios" className="text-sm font-medium text-charcoal/70 hover:text-charcoal transition-colors px-3 sm:px-4 py-2">
            Precios
          </Link>
          <Link data-testid="landing-login-link" to="/login" className="text-sm font-medium text-charcoal/70 hover:text-charcoal transition-colors px-3 sm:px-4 py-2">
            Iniciar sesión
          </Link>
          <Link data-testid="landing-register-link" to="/registro" className="text-sm font-semibold bg-petroleum text-white px-4 sm:px-5 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all">
            Registrar mi empresa
          </Link>
        </div>
      </header>

      <section className="px-6 md:px-12 pt-10 md:pt-16 grid md:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-charcoal leading-[1.05]">
            Cumplimiento laboral y estrategia de talento, sin área de RH.
          </h1>
          <p className="text-base md:text-lg text-charcoal/70 mt-6 max-w-lg">
            Documenta evidencia, evita conflictos laborales y toma mejores decisiones de talento — todo anclado a la Ley Federal del Trabajo.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link data-testid="landing-cta-register" to="/registro" className="bg-terracotta text-white font-semibold px-6 py-3 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all">
              Registrar mi empresa
            </Link>
            <Link data-testid="landing-cta-demo" to="/demo" className="border border-petroleum/20 text-charcoal font-semibold px-6 py-3 rounded-full hover:-translate-y-0.5 hover:shadow-lg hover:border-petroleum/40 transition-all">
              Ver cómo funciona
            </Link>
          </div>
          <p className="text-xs text-charcoal/50 mt-6 max-w-md" data-testid="landing-disclaimer">
            CERH no sustituye la asesoría de un abogado laboral. Es una herramienta de documentación y apoyo a la decisión.
          </p>
        </div>
        <img
          src="https://images.pexels.com/photos/7693692/pexels-photo-7693692.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Equipo de trabajo colaborando"
          className="rounded-2xl shadow-[0_8px_30px_rgba(14,79,79,0.15)] w-full h-80 object-cover"
        />
      </section>

      <section className="px-6 md:px-12 py-10 max-w-4xl mx-auto" data-testid="landing-welcome-video-section">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-charcoal text-center mb-6">
          Conoce CERH
        </h2>
        <div className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(14,79,79,0.15)] aspect-video">
          <iframe
            className="w-full h-full"
            src="https://www.youtube.com/embed/rsy7mIr0DOo"
            title="Bienvenida a CERH"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </section>

      <section className="px-6 md:px-12 py-6 max-w-6xl mx-auto">
        <motion.div
          data-testid="landing-jornada-section"
          className="bg-petroleum rounded-2xl p-8 md:p-10 shadow-[0_20px_50px_rgba(14,79,79,0.25)]"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <span className="inline-flex items-center gap-1.5 bg-white/10 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Nuevo módulo
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white leading-tight max-w-xl">
            Registro de Jornada Laboral, ya resuelto
          </h2>
          <p className="text-white/75 mt-3 max-w-xl text-sm md:text-base">
            La reforma laboral de 2026 exige llevar un control claro de entradas y salidas de tu equipo, con reglas de la STPS que entran en vigor en 2027. CERH ya lo resuelve por ti: tus empleados marcan desde su celular o un kiosko compartido con PIN o QR, y tú ves quién llegó, en tiempo real, sin Excel ni relojes checadores viejos.
          </p>
          <Link data-testid="landing-jornada-cta" to="/demo" className="inline-flex items-center gap-2 mt-6 bg-white text-petroleum font-semibold px-5 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all text-sm">
            Ver cómo funciona <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </motion.div>
      </section>

      <section className="px-6 md:px-12 py-16 max-w-6xl mx-auto grid sm:grid-cols-3 gap-6">
        {[
          { icon: ScrollText, title: "Políticas con evidencia", desc: "Biblioteca de cláusulas alineadas a la LFT, acuses fechados e inmutables." },
          { icon: TrendingUp, title: "Decisiones con costos reales", desc: "Compara despido vs. retención con cifras concretas." },
          { icon: CalendarCheck, title: "Vacaciones sin fricción", desc: "Cálculo automático conforme a los Art. 76 y 78 de la LFT." },
        ].map((f, i) => (
          <div key={i} className="bg-card border border-petroleum/10 rounded-xl p-6 shadow-[0_8px_30px_rgba(14,79,79,0.06)]">
            <f.icon className="w-6 h-6 text-terracotta mb-3" strokeWidth={1.5} />
            <h3 className="font-heading font-semibold text-lg text-charcoal">{f.title}</h3>
            <p className="text-sm text-charcoal/60 mt-2">{f.desc}</p>
          </div>
        ))}
      </section>
      <Footer />
    </div>
  );
}
